import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
//import ttest from 'jstat-esm';

import type {
  BillIndex,
  BillIndexEntry,
  MetricIndex,
  MetricIndexEntry,
  DistrictDict,
  DistrictId,
  MetricAggregates,
  StatsBundle,
} from '@/types.ts';
import { VoteType as VoteTypeConst } from '@/types.ts';

import { fetchBillFile } from '@/domain/bills/fetchBillFile';
import { fetchBillIndex } from '@/domain/bills/fetchBillIndex';
import { fetchMetricIndex } from '@/domain/metrics/fetchMetricIndex';
import { fetchMetricFile } from '@/domain/metrics/fetchMetricFile';
import { fetchDistrictsFile } from '@/domain/districts/fetchDistrictsFile';

/**
 * @fileoverview Data Store for the legislative visualization application.
 *
 * The Data Store manages all application data and data loading operations:
 * - Contains the actual data: districts, bills, metrics, and their metadata
 * - Handles data fetching from files (bills, metrics, districts)
 * - Manages selection state (congressId, billId, metricKey) - these determine
 *   what data to load, so they belong here even though metricKey also affects
 *   visualization
 * - Calculates aggregates and statistics from loaded data
 * - Tracks loading and error states
 * - **Not persisted** - data is loaded fresh on each session
 *
 * This separation allows the application to:
 * - Keep data loading logic separate from presentation logic
 * - Clear data independently of visualization settings
 */

/**
 * Helper type to properly type Zustand hooks with equality function support.
 * This fixes TypeScript inference issues when using middleware with equality functions.
 */
type UseStoreWithEquality<T> = <U>(
  selector: (state: T) => U,
  equalityFn?: (a: U, b: U) => boolean,
) => U;

/********************************************************************
 *
 * CREDIT: leeoniya on GitHub
 *
 * https://gist.github.com/leeoniya/747e00a58cd5980d1a9c8d8e8979c68a
 *
 *********************************************************************/
const P0 = [
  +0.000124818987, -0.001075204047, +0.005198775019, -0.019198292004, +0.059054035642,
  -0.151968751364, +0.319152932694, -0.5319230073, +0.797884560593,
];
const P1 = [
  -0.000045255659, +0.00015252929, -0.000019538132, -0.000676904986, +0.001390604284,
  -0.00079462082, -0.002034254874, +0.006549791214, -0.010557625006, +0.011630447319,
  -0.009279453341, +0.005353579108, -0.002141268741, +0.000535310849, +0.999936657524,
];
function pol(x: number, c: number[]) {
  return c.reduce((r, c) => r * x + c, 0);
}

// integral amount of ND(mean=0, SD=1) returns between 0 and 1
// - gauss(z) = (1 + erf(z * 2**-0.5))/2
function gauss(z: number) {
  if (z === 0) return 0.5;
  const y = Math.abs(z) / 2;
  const p = y >= 3 ? 1 : y < 1 ? pol(y * y, P0) * y * 2 : pol(y - 2, P1);
  return z > 0 ? (1 + p) / 2 : (1 - p) / 2;
}
//console.log(norm(-3)); // ~~ 0.0013
//console.log(norm(-2)); // ~~ 0.023
//console.log(norm(-1)); // ~~ 0.16

// ACM Algorithm #395 (student t-distribution: df as double)
// t-dist : distribution for average of (df+1)-samples from ND(mean=0, SD=1)
// student(t, df) returns
//   integral probability of both side area (< -t) and (t <) of t-dist(df)
const Ps = [-0.4, -3.3, -24, -85.5];
function student(t: number, df: number) {
  const a = df - 0.5;
  const b = 48 * a * a;
  const y0 = (t * t) / df;
  const y = a * (y0 > 1e-6 ? Math.log(y0 + 1) : y0);
  const s = (pol(y, Ps) / (0.8 * y * y + 100 + b) + y + 3) / b + 1;
  return 2 * gauss(-s * y ** 0.5);
}
//console.log(student(1, 1000)); // ~~ 0.32
//console.log(student(2, 1000)); // ~~ 0.046
//console.log(student(3, 1000)); // ~~ 0.0027

// welch's t-test (using values processed below)

function tTestAlt(
  meanX: number,
  meanY: number,
  varX: number,
  varY: number,
  nX: number,
  nY: number,
) {
  const nX1 = nX - 1,
    nY1 = nY - 1;
  const avX = varX / nX,
    avY = varY / nY;
  const t = (meanX - meanY) / (avX + avY) ** 0.5;
  const df = (avX + avY) ** 2 / (avX ** 2 / nX1 + avY ** 2 / nY1);
  const p = student(t, df);
  return { p, t, df, meanX, meanY };
}
//*****************************************************************************

//helper functions for t-testing
function tstat(
  mean_1: number,
  mean_2: number,
  var_1: number,
  var_2: number,
  n_1: number,
  n_2: number,
): number {
  if (
    mean_1 === undefined ||
    mean_2 === undefined ||
    var_1 === undefined ||
    var_2 === undefined ||
    n_1 === undefined ||
    n_2 === undefined
  ) {
    return 0;
  }
  return Math.round((mean_1 - mean_2) / Math.sqrt(var_1 / n_1 + var_2 / n_2));
}

/**
 * State interface for the Data Store.
 *
 * The Data Store manages all application data including:
 * - Selection state (which congress, bill, and metric are active)
 *   - Note: `metricKey` is stored here because it triggers data loading,
 *     even though it also affects visualization
 * - Loaded data (districts with vote and metric data)
 * - Indexes (lists of available bills and metrics)
 * - Loading and error states
 *
 * **Persistence**: This store is NOT persisted - data is loaded fresh on each session.
 * This includes all selection state, loaded data, indexes, and loading/error states.
 *
 * @see useDataStore - Hook to access this store
 */
export type DataStoreState = {
  /** Current congress ID selection */
  congressId: number;
  /** Current bill ID selection */
  billId: string;
  /** Current metric key selection */
  metricKey: string;

  /** Dictionary of all districts with their vote and metric data */
  districts: DistrictDict;
  /** Calculated aggregate statistics from the districts data */
  metricAggregates: MetricAggregates | null;
  /** Precomputed statistics bundled with the metric file (keyed by bill id) */
  metricStatistics: Record<string, StatsBundle> | null;
  /** Metadata for the currently loaded bill */
  billMetadata: { description?: string } | null;
  /** Recommended metric name(s) for the currently loaded bill */
  recommendedMetricKeys: string[] | null;
  /** Recommended bill ID for the currently loaded metric */
  recommendedBillId: string | null;

  /** Whether a bill is currently being loaded */
  loading: boolean;
  /** Error message if bill loading failed */
  error?: string;

  /** Index of all available bills for the current congress */
  billIndex: BillIndex | null;
  /** Whether the bill index has been loaded */
  billIndexLoaded: boolean;
  /** Error message if bill index loading failed */
  billIndexError?: string;

  /** Index of all available metrics for the current congress */
  metricIndex: MetricIndex | null;
  /** Whether the metric index has been loaded */
  metricIndexLoaded: boolean;
  /** Error message if metric index loading failed */
  metricIndexError?: string;

  // Actions

  /** Update both congressId and billId selection atomically */
  setSelection: (congressId: number, billId: string) => void;
  /** Update congress ID selection */
  setCongressId: (congressId: number) => void;
  /** Update bill ID selection */
  setBillId: (billId: string) => void;
  /** Update metric key and load metric data if districts are available */
  setMetricKey: (metricKey: string) => Promise<void>;
  /** Load metric data for the specified congress and metric key, merging into existing districts */
  loadMetric: (congressId: number, metricKey: string) => Promise<void>;
  /** Load bill data from the specified congress and bill ID */
  loadBillFromCongress: (congressId: number, billId: string) => Promise<void>;
  /** Convenience method to load bill using current selection state */
  loadBillFromSelection: () => Promise<void>;
  /** Load the bill index for the specified congress (or current congress if not specified) */
  loadBillIndex: (congressId?: number) => Promise<void>;
  /** Case-insensitive search for a bill in the loaded bill index */
  findInBillIndex: (billId: string) => BillIndexEntry | undefined;
  /** Load the metric index for the specified congress (or current congress if not specified) */
  loadMetricIndex: (congressId?: number) => Promise<void>;
  /** Case-insensitive search for a metric in the loaded metric index */
  findInMetricIndex: (metricKey: string) => MetricIndexEntry | undefined;
  /** Recalculate metric aggregates from the current districts data */
  calculateMetricAggregates: () => void;
  /** Clear all loaded data (districts, aggregates, metadata) but keep selection and indexes */
  reset: () => void;
};

const EMPTY_DISTRICTS: DistrictDict = {} as DistrictDict;

/**
 * Calculate metric aggregates from a district dictionary.
 *
 * Computes statistics including:
 * - Overall metric min/max/average
 * - Vote-specific metric averages (yea, nay, no-vote)
 * - Vote counts (yea, nay, no-vote)
 *
 * @param districts - Dictionary of districts with vote and metric data
 * @returns Aggregated statistics, or null if no districts are present
 */

function calculateVar(x_i: number[], mu: number, N: number): number {
  if (N === 0) return 0;
  const variance = x_i.reduce((acc, val) => acc + Math.pow(val - mu, 2), 0) / N;
  return variance;
}

function calculateAggregatesFromDistricts(districts: DistrictDict): MetricAggregates | null {
  const districtValues = Object.values(districts);

  if (districtValues.length === 0) {
    return null;
  }

  // Calculate vote counts
  let yeaCount = 0;
  let noVoteCount = 0;
  let nayCount = 0;

  for (const data of districtValues) {
    const voteType = data.voteType ?? VoteTypeConst.NO_DATA;
    if (voteType === VoteTypeConst.YEA) yeaCount += 1;
    else if (voteType === VoteTypeConst.NO_VOTE) noVoteCount += 1;
    else if (voteType === VoteTypeConst.NAY) nayCount += 1;
  }

  // Collect all metric values
  const metricValues = districtValues
    .map((d) => d.metricValue)
    .filter((v): v is number => typeof v === 'number' && !isNaN(v));

  // Calculate overall metric aggregates (use 0 if no metric values)
  const metricMax = metricValues.length > 0 ? Math.max(...metricValues) : 0;
  const metricMin = metricValues.length > 0 ? Math.min(...metricValues) : 0;
  const metricAvg =
    metricValues.length > 0
      ? metricValues.reduce((sum, val) => sum + val, 0) / metricValues.length
      : 0;

  // Calculate vote-specific metric averages
  const supportValues = districtValues
    .filter(
      (d) =>
        d.voteType === VoteTypeConst.YEA &&
        typeof d.metricValue === 'number' &&
        !isNaN(d.metricValue),
    )
    .map((d) => d.metricValue!);

  const againstValues = districtValues
    .filter(
      (d) =>
        d.voteType === VoteTypeConst.NAY &&
        typeof d.metricValue === 'number' &&
        !isNaN(d.metricValue),
    )
    .map((d) => d.metricValue!);

  const absenceValues = districtValues
    .filter(
      (d) =>
        d.voteType === VoteTypeConst.NO_VOTE &&
        typeof d.metricValue === 'number' &&
        !isNaN(d.metricValue),
    )
    .map((d) => d.metricValue!);

  let voteSupportMetricAvg =
    supportValues.length > 0
      ? supportValues.reduce((sum, val) => sum + val, 0) / supportValues.length
      : 0;

  if (voteSupportMetricAvg >= 10) {
    voteSupportMetricAvg = Math.round(voteSupportMetricAvg);
  }
  let voteAgainstMetricAvg =
    againstValues.length > 0
      ? againstValues.reduce((sum, val) => sum + val, 0) / againstValues.length
      : 0;

  if (voteAgainstMetricAvg >= 10) {
    voteAgainstMetricAvg = Math.round(voteAgainstMetricAvg);
  }

  let voteAbsenceMetricAvg =
    absenceValues.length > 0
      ? absenceValues.reduce((sum, val) => sum + val, 0) / absenceValues.length
      : 0;
  if (voteAbsenceMetricAvg >= 10) {
    voteAbsenceMetricAvg = Math.round(voteAbsenceMetricAvg);
  }

  //set up stats for t test. Also passing these through to return in case they're useful later
  const alpha = 0.05;

  const metricVar = calculateVar(metricValues, metricAvg, metricValues.length);
  const voteSupportMetricVar = calculateVar(
    supportValues,
    voteSupportMetricAvg,
    supportValues.length,
  );
  const voteAgainstMetricVar = calculateVar(
    againstValues,
    voteAgainstMetricAvg,
    againstValues.length,
  );
  const voteAbsenceMetricVar = calculateVar(
    absenceValues,
    voteAbsenceMetricAvg,
    absenceValues.length,
  );

  const voteSupportMetricTStat = tstat(
    voteSupportMetricAvg,
    metricAvg,
    voteSupportMetricVar,
    metricVar,
    yeaCount,
    districtValues.length,
  );
  const voteAgainstMetricTStat = tstat(
    voteAgainstMetricAvg,
    metricAvg,
    voteAgainstMetricVar,
    metricVar,
    nayCount,
    districtValues.length,
  );
  const voteAbsenceMetricTStat = tstat(
    voteAbsenceMetricAvg,
    metricAvg,
    voteAbsenceMetricVar,
    metricVar,
    noVoteCount,
    districtValues.length,
  );

  const voteSupportMetricTtest = tTestAlt(
    voteSupportMetricAvg,
    metricAvg,
    voteSupportMetricVar,
    metricVar,
    yeaCount,
    districtValues.length,
  ).p;
  console.log('support ttest', voteSupportMetricTtest);
  const voteAgainstMetricTtest = tTestAlt(
    voteAgainstMetricAvg,
    metricAvg,
    voteAgainstMetricVar,
    metricVar,
    nayCount,
    districtValues.length,
  ).p;
  const voteAbsenceMetricTtest = tTestAlt(
    voteAbsenceMetricAvg,
    metricAvg,
    voteAbsenceMetricVar,
    metricVar,
    noVoteCount,
    districtValues.length,
  ).p;

  return {
    metricMax: Math.round(metricMax),
    metricMin: Math.round(metricMin),
    metricAvg: Math.round(metricAvg),
    metricVar: Math.round(metricVar),
    count: districtValues.length,
    alpha: alpha,

    voteSupportMetricAvg: voteSupportMetricAvg,
    voteAgainstMetricAvg: voteAgainstMetricAvg,
    voteAbsenceMetricAvg: voteAbsenceMetricAvg,

    voteSupportMetricVar: voteSupportMetricVar,
    voteAgainstMetricVar: voteAgainstMetricVar,
    voteAbsenceMetricVar: voteAbsenceMetricVar,

    //format of t-test returns is [t stat, p val, df]
    voteSupportMetricTtest: [
      Math.round(voteSupportMetricTStat),
      voteSupportMetricTtest,
      districtValues.length + yeaCount - 2,
    ],
    voteAgainstMetricTtest: [
      Math.round(voteAgainstMetricTStat),
      voteAgainstMetricTtest,
      districtValues.length + nayCount - 2,
    ],
    voteAbsenceMetricTtest: [
      Math.round(voteAbsenceMetricTStat),
      voteAbsenceMetricTtest,
      districtValues.length + noVoteCount - 2,
    ],

    yeaCount,
    noVoteCount,
    nayCount,
  };
}

/**
 * Data Store - Manages application data and data loading.
 *
 * This store handles:
 * - Loading bills, metrics, and district data from files
 * - Managing selection state (congress, bill, metric)
 * - Calculating aggregates from loaded data
 * - Tracking loading and error states
 *
 * The store uses Zustand's devtools middleware for debugging.
 * Data is NOT persisted - it's loaded fresh on each session.
 *
 * @see DataStoreState - Type definition for this store
 * @see useDataStore - Hook to access this store
 */
const dataStore = create<DataStoreState>()(
  devtools((set, get) => ({
    // Defaults
    congressId: 118,
    billId: '',
    metricKey: '',

    // Empty dict until a bill is loaded
    districts: EMPTY_DISTRICTS,
    metricAggregates: null,
    metricStatistics: null,
    billMetadata: null,
    recommendedBillId: null,

    loading: false,

    // Bill Index
    billIndex: null,
    billIndexLoaded: false,

    // Metric Index
    metricIndex: null,
    metricIndexLoaded: false,

    /** Update selection only when it truly changes */
    setSelection: (congressId, billId) =>
      set((s) =>
        s.congressId === congressId && s.billId === billId ? s : { ...s, congressId, billId },
      ),

    setCongressId: (congressId) =>
      set((s) => (s.congressId === congressId ? s : { ...s, congressId })),

    setBillId: (billId) => set((s) => (s.billId === billId ? s : { ...s, billId })),

    /** Load metric data and merge into districts, then recalculate aggregates */
    loadMetric: async (congressId, metricKey) => {
      if (!metricKey) {
        // If metricKey is empty, just clear metric data from districts
        set((s) => {
          const updatedDistricts: DistrictDict = {};
          for (const [id, data] of Object.entries(s.districts)) {
            updatedDistricts[id as DistrictId] = {
              ...data,
              metricValue: undefined,
              metricLabel: undefined,
            };
          }
          return {
            ...s,
            districts: updatedDistricts,
            metricAggregates: calculateAggregatesFromDistricts(updatedDistricts),
            metricStatistics: null,
            recommendedBillId: null,
          };
        });
        return;
      }

      try {
        console.info('[Store] loading metric', { congressId, metricKey });
        const {
          districts: metricDistricts,
          statistics,
          recommendedBillId,
        } = await fetchMetricFile(congressId, metricKey);

        console.log('[Store] loadMetric: fetched statistics keys', Object.keys(statistics || {}));

        // Merge metric data into existing districts
        set((s) => {
          const updatedDistricts: DistrictDict = { ...s.districts };

          // Merge metric data for districts that exist
          for (const [districtId, metricData] of Object.entries(metricDistricts)) {
            if (updatedDistricts[districtId as DistrictId]) {
              const existing = updatedDistricts[districtId as DistrictId];
              // Preserve existing properties (like voteType) and add/update metric data
              updatedDistricts[districtId as DistrictId] = {
                ...existing,
                // Only update metricValue if it's actually a number, rounded to nearest whole integer
                ...(typeof metricData.metricValue === 'number' && {
                  metricValue: metricData.metricValue, //Math.round(metricData.metricValue), //rounding unnecessary here
                }),
                ...(metricData.metricLabel && { metricLabel: metricData.metricLabel }),
                // Preserve other metricData properties but don't overwrite voteType
                ...(metricData.congressId !== undefined && { congressId: metricData.congressId }),
                ...(metricData.districtNum !== undefined && {
                  districtNum: metricData.districtNum,
                }),
              };
            }
          }

          // Also update districts that don't have metric data (set to undefined)
          for (const districtId of Object.keys(updatedDistricts)) {
            if (!metricDistricts[districtId as DistrictId]) {
              updatedDistricts[districtId as DistrictId] = {
                ...updatedDistricts[districtId as DistrictId],
                metricValue: undefined,
                metricLabel: undefined,
              };
            }
          }

          const aggregates = calculateAggregatesFromDistricts(updatedDistricts);
          return {
            ...s,
            districts: updatedDistricts,
            metricAggregates: aggregates,
            metricStatistics: statistics ?? null,
            recommendedBillId: recommendedBillId ?? null,
          };
        });

        console.info('[Store] metric loaded:', metricKey);
      } catch (err: unknown) {
        console.error('[Store] loadMetric error', err);
        // On error, clear metric data
        set((s) => {
          const updatedDistricts: DistrictDict = {};
          for (const [id, data] of Object.entries(s.districts)) {
            updatedDistricts[id as DistrictId] = {
              ...data,
              metricValue: undefined,
              metricLabel: undefined,
            };
          }
          return {
            ...s,
            districts: updatedDistricts,
            metricAggregates: calculateAggregatesFromDistricts(updatedDistricts),
            metricStatistics: null,
            recommendedBillId: null,
          };
        });
      }
    },

    setMetricKey: async (metricKey) => {
      const s = get();
      if (s.metricKey === metricKey) return Promise.resolve();

      // Update metricKey first
      set({ metricKey });

      // Load metric data if metricKey is set and we have districts
      if (metricKey && Object.keys(s.districts).length > 0) {
        await get().loadMetric(s.congressId, metricKey);
      } else if (!metricKey) {
        // Clear metric data if metricKey is empty
        await get().loadMetric(s.congressId, '');
      }
    },

    /** Load /data/congress{Id}/bills/index.json */
    loadBillIndex: async (congressId = get().congressId) => {
      set({ billIndexLoaded: false, billIndexError: undefined });
      try {
        const idx = await fetchBillIndex({ congressId, validate: true, cache: 'no-cache' });
        set({ billIndex: idx, billIndexLoaded: true });
      } catch (e: unknown) {
        const errorMessage = e instanceof Error ? e.message : String(e);
        set({ billIndex: null, billIndexLoaded: true, billIndexError: errorMessage });
      }
    },

    /** Case-insensitive search in the loaded bill index */
    findInBillIndex: (billId: string) => {
      const idx = get().billIndex;
      if (!idx) return undefined;
      const needle = billId.toLowerCase();
      return idx.find((e) => e.id.toLowerCase() === needle);
    },

    /** Load /data/congress{Id}/metrics/index.json */
    loadMetricIndex: async (congressId = get().congressId) => {
      set({ metricIndexLoaded: false, metricIndexError: undefined });
      try {
        const idx = await fetchMetricIndex({ congressId, validate: true, cache: 'no-cache' });
        set({ metricIndex: idx, metricIndexLoaded: true });
      } catch (e: unknown) {
        const errorMessage = e instanceof Error ? e.message : String(e);
        set({ metricIndex: null, metricIndexLoaded: true, metricIndexError: errorMessage });
      }
    },

    /** Case-insensitive search in the loaded metric index */
    findInMetricIndex: (metricKey: string) => {
      const idx = get().metricIndex;
      if (!idx) return undefined;
      const needle = metricKey.toLowerCase();
      return idx.find((e) => e.id.toLowerCase() === needle);
    },

    /** Convenience: load using current selection */
    loadBillFromSelection: async () => {
      const { congressId, billId } = get();
      if (!congressId || !billId) return;
      return get().loadBillFromCongress(congressId, billId);
    },

    /** Fetch ONE bill file and publish the dict directly */
    loadBillFromCongress: async (congressId, billId) => {
      try {
        // Only flip loading if this is a real change
        set((s) => {
          const sameRequest = s.loading && s.congressId === congressId && s.billId === billId;
          return sameRequest ? s : { ...s, loading: true, error: undefined };
        });

        console.info('[Store] loading', { congressId, billId });

        // Load both the bill data and the districts.json file
        const [billResult, validDistrictIds] = await Promise.all([
          fetchBillFile(congressId, billId),
          fetchDistrictsFile(congressId),
        ]);

        const { districts: billDistricts, metadata: billMetadata } = billResult;

        // Create a Set for fast lookup of valid district IDs
        const validDistrictSet = new Set(validDistrictIds);

        // Filter billDistricts to only include districts that exist in districts.json
        // and initialize all valid districts in the dict (with empty data for districts without bill data)
        const districts: DistrictDict = {};

        // First, initialize all valid districts from districts.json
        for (const districtId of validDistrictIds) {
          districts[districtId as DistrictId] = {
            districtId: districtId as DistrictId,
          };
        }

        // Then, merge in bill data for districts that have votes
        for (const [districtId, districtData] of Object.entries(billDistricts)) {
          if (validDistrictSet.has(districtId)) {
            districts[districtId as DistrictId] = {
              ...districts[districtId as DistrictId],
              ...districtData,
            };
          } else {
            console.warn(
              `[Store] District ${districtId} from bill data not found in districts.json, skipping`,
            );
          }
        }

        // Drop stale responses if selection changed mid-flight
        set((s) => {
          const stale = s.congressId !== congressId || s.billId !== billId;
          if (stale) {
            console.info('[Store] stale response ignored', {
              expected: { congressId, billId },
              current: { congressId: s.congressId, billId: s.billId },
            });
            return s;
          }
          return {
            ...s,
            districts,
            billMetadata,
            recommendedMetricKeys: billMetadata?.recommendedMetricKeys ?? null,
            loading: false,
            metricAggregates: calculateAggregatesFromDistricts(districts),
          };
        });

        console.info('[Store] districts loaded:', Object.keys(districts).length);

        // Load metric data if metricKey is set
        const currentState = get();
        if (
          currentState.metricKey &&
          currentState.congressId === congressId &&
          currentState.billId === billId
        ) {
          await get().loadMetric(congressId, currentState.metricKey);
        }
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        set((s) => {
          const stale = s.congressId !== congressId || s.billId !== billId;
          return stale
            ? s
            : {
                ...s,
                districts: EMPTY_DISTRICTS,
                billMetadata: null,
                recommendedMetricKeys: null,
                loading: false,
                error: errorMessage,
              };
        });
        console.error('[Store] loadBillFromCongress error', err);
      }
    },

    /** Calculate aggregate statistics from current districts */
    calculateMetricAggregates: () => {
      const { districts } = get();
      set({ metricAggregates: calculateAggregatesFromDistricts(districts) });
    },

    /** Clear viz payload (keep selection and index) */
    reset: () =>
      set((s) => ({
        ...s,
        districts: EMPTY_DISTRICTS,
        metricAggregates: null,
        billMetadata: null,
        recommendedMetricKeys: null,
        loading: false,
        error: undefined,
      })),
  })),
);

/**
 * Hook to access the Data Store.
 *
 * Use this hook in React components to read and update data store state.
 * Supports equality functions for optimized re-renders.
 *
 * @example
 * ```tsx
 * const districts = useDataStore((state) => state.districts);
 * const loadBill = useDataStore((state) => state.loadBillFromCongress);
 * ```
 */
export const useDataStore = dataStore as unknown as UseStoreWithEquality<DataStoreState>;
